import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import { LinkedJobRow } from './LinkedJobRow';
import { LinkedJobsPanelProps } from './LinkedJobsPanel.props';
import { PartHistoryDataType } from '../../../../../@types/part.type';
import { Panel } from '../../../../../components/atoms/Panel';

const headerSx = {
  fontSize: '11px',
  py: 0.75,
  px: 1.5,
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

/** Use partHistory just for the unique serviceIds — live status comes from useGetServiceById per row. */
const uniqueServiceIds = (history: PartHistoryDataType[]): number[] => {
  const seen = new Map<number, string>();
  for (const entry of history) {
    if (!entry.serviceId) continue;
    const existing = seen.get(entry.serviceId);
    if (!existing || (entry.createdAt ?? '') > existing) {
      seen.set(entry.serviceId, entry.createdAt ?? '');
    }
  }
  return Array.from(seen.entries())
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([id]) => id);
};

export const LinkedJobsPanel: React.FC<LinkedJobsPanelProps> = ({ partHistory, sx }) => {
  const ids = useMemo(() => uniqueServiceIds(partHistory), [partHistory]);

  return (
    <Panel title={`Linked Jobs${ids.length ? ` · ${ids.length}` : ''}`} sx={sx}>
      {ids.length === 0 ? (
        <Typography
          sx={{ fontSize: '13px', color: 'text.secondary', py: 1.5, textAlign: 'center' }}
        >
          No jobs linked to this part
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Linked jobs">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Job ID</TableCell>
                <TableCell sx={headerSx}>Status</TableCell>
                <TableCell sx={headerSx}>Type</TableCell>
                <TableCell sx={headerSx}>Location</TableCell>
                <TableCell sx={headerSx}>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ids.map((id, idx) => (
                <LinkedJobRow key={id} serviceId={id} zebra={idx % 2 === 1} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Panel>
  );
};
